import { faEye, faTrash, faGripVertical } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState, useEffect } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';

interface Image {
  id: number;
  url: string;
}

interface ImageManagerWithOrderProps {
  images: Image[];
  propertyId: number;
  onImageDeleted: () => void;
}

export function ImageManagerWithOrder({
  images,
  onImageDeleted,
}: ImageManagerWithOrderProps) {
  const [orderedImages, setOrderedImages] = useState<Image[]>(images);
  const [selectedImages, setSelectedImages] = useState<number[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const { toast } = useToast();

  // Atualizar quando images mudar APENAS se a quantidade mudou (upload/delete)
  useEffect(() => {
    // Só atualiza se o número de imagens mudou (upload ou delete)
    if (images.length !== orderedImages.length) {
      setOrderedImages(images);
    }
  }, [images, orderedImages.length]);

  // Expor método para pegar ordem atual
  useEffect(() => {
    // Criar função que retorna a ordem atual
    const getCurrentOrder = () => orderedImages.map(img => img.id);
    // Armazenar referência para ser chamada no submit
    (window as any).__getCurrentPhotoOrder = getCurrentOrder;
  }, [orderedImages]);

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (targetIndex: number) => {
    if (draggedIndex === null) return;

    const newImages = [...orderedImages];
    const [draggedItem] = newImages.splice(draggedIndex, 1);
    newImages.splice(targetIndex, 0, draggedItem);

    setOrderedImages(newImages);
    setDraggedIndex(null);
  };

  const handleCheckboxChange = (imageId: number, checked: boolean) => {
    if (checked) {
      setSelectedImages([...selectedImages, imageId]);
    } else {
      setSelectedImages(selectedImages.filter((id) => id !== imageId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedImages(orderedImages.map((img) => img.id));
    } else {
      setSelectedImages([]);
    }
  };

  const confirmDeleteMultiple = () => {
    if (selectedImages.length === 0) {
      toast({
        title: 'Atenção',
        description: 'Selecione pelo menos uma imagem para excluir.',
        variant: 'destructive',
      });
      return;
    }
    setConfirmOpen(true);
  };

  const handleDeleteMultiple = async () => {
    try {
      const deletePromises = selectedImages.map((imageId) =>
        fetch(
          `${import.meta.env.VITE_URL_ENDPOINT}/api/upload/files/${imageId}`,
          { method: 'DELETE' }
        )
      );

      await Promise.all(deletePromises);

      toast({
        title: 'Sucesso!',
        description: `${selectedImages.length} imagem(ns) excluída(s).`,
      });

      setSelectedImages([]);
      onImageDeleted();
    } catch {
      toast({
        title: 'Erro',
        description: 'Erro ao deletar imagens',
        variant: 'destructive',
      });
    } finally {
      setConfirmOpen(false);
    }
  };

  const moveUp = (index: number) => {
    if (index === 0) return;

    const newImages = [...orderedImages];
    [newImages[index - 1], newImages[index]] = [
      newImages[index],
      newImages[index - 1],
    ];
    setOrderedImages(newImages);
  };

  const moveDown = (index: number) => {
    if (index === orderedImages.length - 1) return;

    const newImages = [...orderedImages];
    [newImages[index], newImages[index + 1]] = [
      newImages[index + 1],
      newImages[index],
    ];
    setOrderedImages(newImages);
  };

  if (orderedImages.length === 0) {
    return (
      <div className="mb-4 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-8 text-center">
        <p className="text-gray-500">Nenhuma imagem adicionada ainda</p>
      </div>
    );
  }

  return (
    <>
      <div className="mb-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="select-all"
                checked={
                  selectedImages.length === orderedImages.length &&
                  orderedImages.length > 0
                }
                onCheckedChange={handleSelectAll}
              />
              <Label htmlFor="select-all" className="text-sm text-gray-700">
                Selecionar todas ({orderedImages.length})
              </Label>
            </div>
            {selectedImages.length > 0 && (
              <span className="text-sm font-medium text-blue-600">
                {selectedImages.length} selecionada(s)
              </span>
            )}
          </div>
          {selectedImages.length > 0 && (
            <Button
              onClick={confirmDeleteMultiple}
              variant="destructive"
              size="sm"
            >
              <FontAwesomeIcon icon={faTrash} className="mr-2" />
              Excluir Selecionadas
            </Button>
          )}
        </div>

        <p className="mb-3 text-sm text-gray-500">
          💡 Arraste as imagens para reordenar. A primeira será a foto principal
          do card.
        </p>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {orderedImages.map((image, index) => (
            <div
              key={image.id}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(index)}
              className={`group relative cursor-move rounded-lg border-2 ${
                draggedIndex === index
                  ? 'border-blue-500 opacity-50'
                  : selectedImages.includes(image.id)
                  ? 'border-blue-500 ring-2 ring-blue-200'
                  : 'border-gray-300'
              } overflow-hidden bg-white shadow-md transition-all hover:shadow-lg`}
            >
              <div className="aspect-square w-full">
                <img
                  src={image.url}
                  alt={`Imagem ${index + 1}`}
                  className="h-full w-full object-cover"
                />
              </div>

              {/* Checkbox */}
              <div className="absolute left-2 top-2 z-10">
                <Checkbox
                  checked={selectedImages.includes(image.id)}
                  onCheckedChange={(checked) =>
                    handleCheckboxChange(image.id, checked as boolean)
                  }
                  className="h-5 w-5 border-2 border-white bg-white/90 shadow-md"
                />
              </div>

              {/* Número da ordem */}
              <div className="absolute right-2 top-2 rounded-full bg-black/70 px-2 py-1 text-xs font-bold text-white">
                {index + 1}
              </div>

              {/* Ícone de drag */}
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 opacity-0 transition-opacity group-hover:opacity-100">
                <FontAwesomeIcon
                  icon={faGripVertical}
                  className="text-white"
                  size="lg"
                />
              </div>

              {/* Botões de ação */}
              <div className="absolute bottom-2 right-2 flex gap-1">
                <button
                  type="button"
                  onClick={() => setPreviewImage(image.url)}
                  className="rounded-full bg-white/90 p-1.5 text-gray-700 shadow-md hover:bg-white"
                  title="Visualizar"
                >
                  <FontAwesomeIcon icon={faEye} className="h-3 w-3" />
                </button>
              </div>

              {/* Botões de reordenação */}
              <div className="absolute bottom-2 left-2 flex flex-col gap-1">
                <button
                  type="button"
                  onClick={() => moveUp(index)}
                  disabled={index === 0}
                  className="rounded-full bg-white/90 p-1 text-gray-700 shadow-md hover:bg-white disabled:opacity-50"
                  title="Mover para cima"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3 w-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 15l7-7 7 7"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => moveDown(index)}
                  disabled={index === orderedImages.length - 1}
                  className="rounded-full bg-white/90 p-1 text-gray-700 shadow-md hover:bg-white disabled:opacity-50"
                  title="Mover para baixo"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3 w-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir {selectedImages.length} imagem(ns)?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não poderá ser desfeita. As imagens serão permanentemente
              removidas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteMultiple}>
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {previewImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
          onClick={() => setPreviewImage(null)}
        >
          <img
            src={previewImage}
            alt="Preview da imagem"
            className="max-h-[80vh] max-w-[80vw] object-contain"
          />
          <button
            onClick={() => setPreviewImage(null)}
            className="absolute right-4 top-4 rounded-full bg-white p-2 text-red-500 shadow hover:bg-red-50"
            title="Fechar"
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
      )}
    </>
  );
}
